import {
    CostCenter,
    CostCenter2,
    Employee,
    Expense,
    ExpensePaymentFlowInformationEnum,
    ExpenseRequestTypeEnum,
    ExpenseStatusEnum,
    RightEnum,
    Transaction,
    Vendor,
    VendorTypeEnum,
} from "@finway-group/shared/lib/models"
import React from "react"
import { useTranslation } from "react-i18next"

import { CreditCardLink } from "Components/creditCard/CreditCardLink"
import EmployeeLink from "Components/employeeLink"
import ExpenseStatusTag from "Components/expenseStatusTag"
import ExpenseTransactionStatusTag from "Components/expenseTransactionStatusTag"
import { useCardByExpense } from "Shared/hooks/creditCard.hooks"
import { useEmployee } from "Shared/queries/employee.queries"
import { AuthzService, ExpenseService } from "Shared/services"
import { getNextApproverId, getNextExpenseEmbeddedApprover } from "Shared/utils/approvalProcess.utils"
import { isExpenseArchived } from "Shared/utils/expense.utils"
import { formatExpenseDate } from "../../../../../shared/utils/date.utils"
import { UserCreditor } from "../../../../../shared/utils/vendor.utils"
import { CostCenterField } from "./costCenter.field"
import { CostCenter2Field } from "./costCenter2.field"
import { ExpenseAccountField } from "./expenseAccount.field"
import { InvoiceDateField } from "./invoiceDate.field"
import { InvoiceDatePaidAtField } from "./invoiceDatePaidAt.field"
import { InvoiceDueDateField } from "./invoiceDueDate.field"
import { InvoiceNumberField } from "./invoiceNumber.field"
import { PerformanceDateField } from "./performanceDate.field"
import { PostingPeriodField } from "./postingPeriod.field"
import { SubscriptionField } from "./subscription.field"
import { VendorField } from "./vendor.field"
import { WorkflowField } from "./workflow.field"

interface PurchaseTabProps {
    expense: Expense
    requestedBy: Employee
    costCenter?: CostCenter
    costCenter2?: CostCenter2
    vendorCreditor?: Vendor
    currentExpenseCreditor: UserCreditor | Vendor | undefined
    requestType: ExpenseRequestTypeEnum | undefined
    setIsExpenseUpdating?: React.Dispatch<React.SetStateAction<boolean>>
    setSelectedExpense?: (expense: Expense | undefined) => void
    isExpenseUpdating?: boolean
    isOcrUploading?: boolean
    isReadOnly?: boolean
}

export const PurchaseTabContent = ({
    expense,
    requestedBy,
    costCenter,
    costCenter2,
    vendorCreditor,
    currentExpenseCreditor,
    requestType,
    isReadOnly,
    isOcrUploading,
    isExpenseUpdating,
    setIsExpenseUpdating,
    setSelectedExpense,
}: PurchaseTabProps) => {
    const { t } = useTranslation()
    const { status } = expense

    const archived = isExpenseArchived(expense)
    const isEditableBasedOnArchive = ExpenseService.isExpenseEditableBasedOnArchive(archived)

    const shouldShowInvoiceFields =
        (expense.status === ExpenseStatusEnum.INVOICE_PENDING || expense.status === ExpenseStatusEnum.APPROVED) && !expense.deleted && !isReadOnly && isEditableBasedOnArchive
    const isInvoiceFieldRequired = expense.status === ExpenseStatusEnum.APPROVED && expense.paymentFlowInformation !== ExpensePaymentFlowInformationEnum.REPORTING_ONLY
    const { card } = useCardByExpense(expense)

    const isUserCreditor = expense?.vendorType === VendorTypeEnum.USER

    const areInputsDisabled = isOcrUploading || isExpenseUpdating
    const paymentTerm = !isUserCreditor ? (currentExpenseCreditor as Vendor)?.rule?.paymentTerm : undefined
    const { data: nextApprover } = useEmployee(getNextApproverId(expense.approvalProcesses))
    const nextExpenseEmbeddedApprover = getNextExpenseEmbeddedApprover(expense.approvalProcesses)

    return (
        <ul className="description-list pb-10" id="expense-purchase-data-list">
            <li>
                <span className="title">{t("label:status")}</span>
                <span className="text">
                    <ExpenseStatusTag status={status} />
                </span>
            </li>
            <li>
                <span className="title">{t("label:purchased")}</span>
                <span data-testid="purchaseDateExpenseForm" className="text">
                    {formatExpenseDate(expense.datePurchased)}
                </span>
            </li>
            <PerformanceDateField expense={expense} />
            <li>
                <span className="title">{t("label:requested_by")}</span>
                <span className="text truncate max-w-250">
                    <EmployeeLink employee={requestedBy} expenseExtendedRefUser={expense.requestedBy} />
                </span>
            </li>
            {nextApprover && (
                <li>
                    <span data-testid="finway:purchase:nextApprover-label" className="title">
                        {t("label:next_approver")}
                    </span>
                    <span className="text truncate max-w-250">
                        <EmployeeLink employee={nextApprover} expenseExtendedRefUser={nextExpenseEmbeddedApprover} allowReplacementIcon />
                    </span>
                </li>
            )}
            <WorkflowField expense={expense} />
            <CostCenterField
                expense={expense}
                setIsExpenseUpdating={setIsExpenseUpdating}
                setSelectedExpense={setSelectedExpense}
                costCenter={costCenter}
                requestedBy={requestedBy}
                isReadOnly={!!isReadOnly}
                areInputsDisabled={!!areInputsDisabled}
            />
            <CostCenter2Field expense={expense} costCenter2={costCenter2} />
            <ExpenseAccountField expense={expense} isInputDisabled={areInputsDisabled || isReadOnly} isLoading={isExpenseUpdating} />
            <VendorField
                expense={expense}
                setIsExpenseUpdating={setIsExpenseUpdating}
                setSelectedExpense={setSelectedExpense}
                currentExpenseCreditor={currentExpenseCreditor}
                vendorCreditor={vendorCreditor}
                isReadOnly={isReadOnly}
                areInputsDisabled={!!areInputsDisabled}
            />
            <CreditCardLink card={card} />
            <SubscriptionField expense={expense} />
            {expense.paymentFlowInformation && (
                <li>
                    <span className="title">{t("label:payment_flow_information.title")}</span>
                    <span className="text">
                        <span>{t(`label:payment_flow_information.${expense.paymentFlowInformation}`)}</span>
                    </span>
                </li>
            )}
            {requestType !== ExpenseRequestTypeEnum.PURCHASE && (
                <>
                    <InvoiceNumberField
                        editable={shouldShowInvoiceFields}
                        expense={expense}
                        isLoading={areInputsDisabled}
                        required={isInvoiceFieldRequired}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                    />
                    <InvoiceDateField
                        expense={expense}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                        areInputsDisabled={!!areInputsDisabled}
                        shouldShowInvoiceFields={shouldShowInvoiceFields}
                        isInvoiceFieldRequired={isInvoiceFieldRequired}
                        paymentTerm={paymentTerm}
                    />
                    <InvoiceDueDateField
                        expense={expense}
                        areInputsDisabled={!!areInputsDisabled}
                        shouldShowInvoiceFields={shouldShowInvoiceFields}
                        isInvoiceFieldRequired={isInvoiceFieldRequired}
                        paymentTerm={paymentTerm}
                        setIsExpenseUpdating={setIsExpenseUpdating}
                    />
                    <InvoiceDatePaidAtField expense={expense} setIsExpenseUpdating={setIsExpenseUpdating} areInputsDisabled={!!areInputsDisabled} />
                    <PostingPeriodField isLoading={areInputsDisabled} expense={expense} editable={shouldShowInvoiceFields} />
                    {expense.isGobdCompliantInvoice() && (
                        <li>
                            <span className="title">{t("label:gobd.invoice_compliance_date")}</span>
                            <span className="text">{expense.gobdInvoiceComplianceDate ? formatExpenseDate(expense.gobdInvoiceComplianceDate) : "-"}</span>
                        </li>
                    )}
                    {expense.matchedTransactions?.length > 0 && AuthzService.isRightGrantedForLoggedInUser(RightEnum.TRANSACTION__ALL__READ) && (
                        <li>
                            <span className="title">{t("label:matching.transaction_status")}</span>
                            <span className="text">
                                <ExpenseTransactionStatusTag expense={expense as Expense & { matchedTransactions: Array<Transaction> }} />
                            </span>
                        </li>
                    )}
                </>
            )}
        </ul>
    )
}
